import i18n, { t } from 'i18next';
import { isEmpty } from 'lodash';

import { getCountryNameFromCode } from './CountryUtils';

type Address = {
    name?: string;
    addressLine1?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    countryCode?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
};

export function formatAddress(
    addressObject: Address,
    options: {
        showName?: boolean;
        showCountry?: boolean;
        fallbackWhenEmpty?: string;
    } = {}
) {
    const language = i18n.language;

    const optionsWithDefaults = {
        showName: true,
        showCountry: true,
        fallbackWhenEmpty: t('Not specified'),
        ...options,
    };

    if (!addressObject || isEmpty(addressObject)) {
        return optionsWithDefaults.fallbackWhenEmpty;
    }

    const namePrefix = optionsWithDefaults.showName && addressObject.name ? addressObject.name : '';
    let formattedAddress = '';

    if (addressObject.addressLine1) {
        formattedAddress = addressObject.addressLine1;
    }

    if (addressObject.city && addressObject.zipCode) {
        formattedAddress = formattedAddress
            ? `${formattedAddress}, ${addressObject.zipCode} ${addressObject.city}`
            : `${addressObject.zipCode} ${addressObject.city}`;
    } else if (addressObject.city) {
        formattedAddress = formattedAddress ? `${formattedAddress}, ${addressObject.city}` : addressObject.city;
    } else if (addressObject.zipCode) {
        formattedAddress = formattedAddress ? `${formattedAddress}, ${addressObject.zipCode}` : addressObject.zipCode;
    }

    if (optionsWithDefaults.showCountry && (addressObject.countryCode || addressObject.country)) {
        formattedAddress = formattedAddress
            ? `${formattedAddress}, ${getCountryNameFromCode(
                  addressObject.countryCode,
                  language,
                  addressObject.country
              )}`
            : getCountryNameFromCode(addressObject.countryCode, language, addressObject.country);
    }

    if (namePrefix) {
        formattedAddress = formattedAddress ? `${namePrefix} - ${formattedAddress}` : namePrefix;
    }

    return formattedAddress !== '' ? formattedAddress : optionsWithDefaults.fallbackWhenEmpty;
}

export function formatAddressName(addressObject: Address) {
    if (addressObject?.name) {
        return addressObject.name;
    }
    if (addressObject?.company) {
        return addressObject.company;
    }
    if (addressObject?.firstName && addressObject?.lastName) {
        return `${addressObject.firstName} ${addressObject.lastName}`;
    }
    return t('Not specified');
}

export function formatAddressShort(
    addressObject: Address,
    options: {
        showName?: boolean;
        showCountry?: boolean;
        fallbackWhenEmpty?: string;
    } = {}
) {
    const language = i18n.language;

    const optionsWithDefaults = {
        showName: true,
        showCountry: false,
        fallbackWhenEmpty: t('Not specified'),
        ...options,
    };

    if (!addressObject || isEmpty(addressObject)) {
        return optionsWithDefaults.fallbackWhenEmpty;
    }

    if (optionsWithDefaults.showName && addressObject.name) {
        return addressObject.name;
    }

    let formattedAddress = '';
    if (addressObject.city && addressObject.zipCode) {
        formattedAddress = `${addressObject.zipCode} ${addressObject.city}`;
    } else if (addressObject.city) {
        formattedAddress = addressObject.city;
    } else if (addressObject.zipCode) {
        formattedAddress = addressObject.zipCode;
    }

    if (optionsWithDefaults.showCountry && (addressObject.countryCode || addressObject.country)) {
        formattedAddress = formattedAddress
            ? `${formattedAddress}, ${getCountryNameFromCode(
                  addressObject.countryCode,
                  language,
                  addressObject.country
              )}`
            : getCountryNameFromCode(addressObject.countryCode, language, addressObject.country);
    }

    return formattedAddress !== '' ? formattedAddress : optionsWithDefaults.fallbackWhenEmpty;
}
